import {faPencil, faUpload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  ActionIcon,
  Button,
  Group,
  MantineSpacing,
  Paper,
  Stack,
  StyleProp,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {Dispatch, FC, SetStateAction, useMemo, useState} from 'react';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {DocumentsService} from '../../api/generated/DocumentsService';
import {
  PagedDataTableConfig,
  usePagedDataTable,
} from '../../hooks/paged-data-table/use-paged-data-table';
import {DocumentCreate, DocumentUpdate} from './document-create-update';
import classes from '../job-archive/job-archive.module.css';
import {ROUTES} from '../../routes';
import {useJobStore} from '../job-store';
import {renderedDateFormat} from '../../utils/luxon';
import {AnchorIcon} from '../../components/anchor-icon.tsx';
import {
  AdvancedQueryPage,
  DocumentDetailDto,
  Response,
} from '../../api/generated/index.defs.ts';
import {faTrash} from '@fortawesome/pro-solid-svg-icons';
import {onClickDelete, onConfirmDelete} from './on-delete-actions.ts';

type JobDocumentListingProps = {
  m?: StyleProp<MantineSpacing>;
  id: number;
  review?: boolean;
};

export const JobDocumentListing: FC<JobDocumentListingProps> = ({
  m,
  id,
  review,
}) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [documentId, setDocumentId] = useState<number | undefined>();

  const {fetchJob, isJobReadonly} = useJobStore((state) => ({
    fetchJob: state.fetchJob,
    isJobReadonly: state.isJobReadonly,
  }));

  const pagedFetchAction = review
    ? DocumentsService.getAllReviewsByJobId
    : DocumentsService.getAllByJobId;

  const tableConfig = useMemo(
    () =>
      TableConfig({
        jobId: id,
        setDocumentId,
        setUpdateOpen,
        pagedFetchAction,
        fetchJob,
        isJobReadonly,
        review,
      }),
    [fetchJob, id, isJobReadonly, pagedFetchAction, review],
  );

  const pagedDataTable = usePagedDataTable(pagedFetchAction, tableConfig, {
    additionalParams: {
      id: id,
    },
    renderActions: (
      <>
        {!isJobReadonly && !review && (
          <Button onClick={() => setCreateOpen(true)}>
            <Group gap='xs'>
              <FontAwesomeIcon icon={faUpload} /> Upload File
            </Group>
          </Button>
        )}
      </>
    ),
  });

  return (
    <Stack m={m}>
      {pagedDataTable}
      <DocumentCreate jobId={id} open={createOpen} setOpen={setCreateOpen} />
      <DocumentUpdate
        open={updateOpen}
        setOpen={setUpdateOpen}
        id={documentId}
        jobId={id}
      />
    </Stack>
  );
};

type TableConfigProps = {
  jobId: number;
  setDocumentId: Dispatch<SetStateAction<number | undefined>>;
  setUpdateOpen: Dispatch<SetStateAction<boolean>>;
  pagedFetchAction: (
    params: any,
  ) => Promise<Response<AdvancedQueryPage<DocumentDetailDto>>>;
  fetchJob: (jobId: number) => Promise<void>;
  isJobReadonly?: boolean | undefined;
  review?: boolean | undefined;
};

const TableConfig = ({
  jobId,
  setDocumentId,
  setUpdateOpen,
  pagedFetchAction,
  fetchJob,
  isJobReadonly,
  review,
}: TableConfigProps) => {
  const isReadonly = (item: DocumentDetailDto) => {
    return review ? isJobReadonly || item.isReadonly : isJobReadonly;
  };

  return PagedDataTableConfig(pagedFetchAction, {
    columns: [
      {
        title: '',
        accessor: '',
        render: (item) => (
          <Tooltip label='Edit Document'>
            <ActionIcon
              disabled={isReadonly(item)}
              className='clear'
              onClick={() => {
                setDocumentId(item.id);
                setUpdateOpen(true);
              }}
              aria-label='Edit Document'
            >
              <FontAwesomeIcon icon={faPencil} />
            </ActionIcon>
          </Tooltip>
        ),
        width: 40,
      },
      {
        title: '',
        accessor: '',
        render: (item) => (
          <Tooltip label='Delete Document'>
            <ActionIcon
              disabled={isReadonly(item)}
              className='clear'
              aria-label='Delete Document'
              onClick={() =>
                onClickDelete({
                  title: <Text fw={600}>Are you sure?</Text>,
                  onConfirm: async () =>
                    await onConfirmDelete(Number(item.id), jobId, fetchJob),
                  labels: {cancel: 'Cancel', confirm: 'Delete'},
                })
              }
            >
              <FontAwesomeIcon icon={faTrash} />
            </ActionIcon>
          </Tooltip>
        ),
        width: 80,
      },
      {
        title: 'Document ID',
        accessor: 'id',
      },
      {
        accessor: 'name',
        render: (item) => (
          <AnchorIcon href={item.url} target='_blank' rel='noopener noreferrer'>
            {item.name}
          </AnchorIcon>
        ),
        sortable: true,
      },
      {
        accessor: 'codeDescription',
        title: 'Type',
        sortable: true,
      },
      {
        accessor: 'createdByUserLastName',
        title: 'Entered By',
        render: (item) =>
          `${item.createdByUserFirstName} ${item.createdByUserLastName}`,
        sortable: true,
      },
      {
        title: 'Date',
        accessor: 'createdDate',
        render: (item) => renderedDateFormat(item.createdDate),
        sortable: true,
      },
    ],
    searchFieldNames: ['name', 'codeDescription'],
    defaultSort: {
      column: 'createdDate',
      direction: 'DESC',
    },
    refetchEvent: 'document-listing',
  });
};

export const JobArchiveDocumentsListing: FC = () => {
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);

  return (
    <Paper>
      <Title fz='sm' className={classes.title}>
        Documents
      </Title>
      <JobDocumentListing m='16 32' id={id} />
    </Paper>
  );
};
